import { z } from "zod";

import { API_URL } from "@/config";

/**
 * Server-driven login mode.
 *
 * `ssoLoginEnabled` lives in tenant info, but `/auth/tenant-info` requires a
 * bearer token and the SSO-vs-password decision gates the LOGIN route tree —
 * it must be made before any session exists. The backend therefore serves the
 * single bit publicly at `GET /auth/login-config`.
 *
 * Answered per HOST. SSO providers are registered against the subdomain the
 * user is on, so the backend stores this flag per host and resolves it for
 * whichever host the request came in on. A tenant reachable at two subdomains
 * can therefore be SSO on one and email/password on the other — nothing here
 * needs to know that, beyond keying the cache by host (below).
 *
 * Additive by design: `true` means this host uses SSO; `false` means the
 * server has no opinion, and isSSOEnabled() then applies the baked-in
 * SSO_ENABLED allowlist. So the flag turns SSO on for a host, never off, and
 * any lookup failure degrades to exactly the pre-flag behaviour.
 *
 * Bootstrap cost is paid only where it buys correctness (see index.tsx):
 *   * auth routes, or a first-ever visit -> await the network
 *   * anywhere else -> prime from cache synchronously, refresh after render
 */

// Keyed by host because the flag itself is host-scoped server-side: a tenant
// reachable at two subdomains can be SSO on one and email/password on the
// other, so one shared key would serve the wrong mode to whichever host
// asked second.
const loginModeCacheKey = (host: string): string => `ssoLoginEnabled:${host}`;
const LOGIN_MODE_FETCH_TIMEOUT_MS = 3000;

const LoginConfigResponseSchema = z.object({ ssoLoginEnabled: z.boolean().optional() });

/**
 * Route prefixes whose rendering depends on the login mode — i.e. every route
 * that Routes.tsx registers inside the `ssoMode ? … : …` branch, since those
 * exist in one mode and not the other:
 *
 *   SSO branch      /login/*, /register, /register/:token
 *   password branch /login, /forgot, /register/:token, /reset/:token,
 *                   and on freemium /contactus, /register, /login/social,
 *                   /login/:token, /verified, /register/callback/google
 *
 * Keep in sync with the conditional in Routes.tsx.
 */
export const AUTH_ROUTE_PREFIXES = [
  "/login",
  "/login-redirect",
  "/register",
  "/forgot",
  "/reset",
  "/verified",
  "/contactus",
] as const;

/**
 * Whether the login mode decides what renders at `pathname`.
 *
 * The tenant is always a subdomain, so this path is already the one Routes.tsx
 * matches against — there is no mount prefix to strip.
 */
export const isAuthRoute = (pathname: string): boolean =>
  // Whole segments only: a raw startsWith would claim /registers for /register
  // and /logins for /login.
  AUTH_ROUTE_PREFIXES.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`));

let ssoLoginEnabledForHost = false;

export const isSSOLoginEnabledForHost = (): boolean => ssoLoginEnabledForHost;

/** `null` means "never fetched on this device", which is distinct from a cached `false`. */
const readCachedLoginMode = (host: string): boolean | null => {
  try {
    const cached = localStorage.getItem(loginModeCacheKey(host));
    if (cached === "true") return true;
    if (cached === "false") return false;
    return null;
  } catch {
    return null;
  }
};

const writeCachedLoginMode = (host: string, value: boolean): void => {
  try {
    // Both values are stored explicitly so a cached `false` stays
    // distinguishable from "no answer yet" — that difference decides whether
    // the bootstrap has to block on the network.
    localStorage.setItem(loginModeCacheKey(host), String(value));
  } catch {
    // Storage unavailable (privacy mode, blocked site data): the fresh value
    // still applied for this load; only the offline fallback is lost.
  }
};

/**
 * Whether the bootstrap must block on the network before rendering.
 *
 * Hoisted out of index.tsx so the decision is testable without importing the
 * app entry (which has module-scope side effects and is excluded from coverage).
 *
 * Blocks in two cases, and only these:
 *   * an auth route — the login mode picks between the SuperTokens UI and the
 *     email/password form, so the wrong answer renders the wrong page;
 *   * a signed-in user with no cached answer — initializeSuperTokens() branches
 *     on the mode at module scope and configures Session differently, so
 *     guessing would hand an SSO tenant's user the self-serve session config.
 *     Every already-signed-in user is in this state the first load after this
 *     ships, which is exactly when it must not be guessed.
 *
 * A signed-OUT first visit to a normal route renders immediately on a primed
 * `false`: ssoMode gates only the auth-route branch in Routes.tsx, so nothing
 * on the page depends on it, and the post-render refresh settles the cache for
 * the next load. That keeps a slow or dead backend from holding first paint on
 * an empty #root.
 */
export const shouldAwaitFreshLoginMode = (
  hasCachedMode: boolean,
  hasStoredUser: boolean,
  pathname: string
): boolean => isAuthRoute(pathname) || (!hasCachedMode && hasStoredUser);

/**
 * Apply the cached login mode synchronously, with no network call.
 *
 * Returns whether a cached answer existed — false means this device has never
 * successfully read the flag, so the caller must await {@link hydrateLoginMode}
 * instead of trusting the default.
 */
export const primeLoginModeFromCache = (host: string): boolean => {
  const cached = readCachedLoginMode(host);
  ssoLoginEnabledForHost = cached ?? false;
  return cached !== null;
};

const fetchLoginMode = async (host: string): Promise<boolean> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), LOGIN_MODE_FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(`${API_URL}/auth/login-config`, {
      headers: { "x-tenant": host },
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`login-config responded ${response.status}`);
    }
    const data: unknown = await response.json();
    // Validated rather than cast: this is the one endpoint called with raw
    // fetch instead of the api helper (which mutates axios.defaults at module
    // scope and so cannot be pulled into the entry chunk), so it would
    // otherwise skip the zod layer every other response goes through.
    const parsed = LoginConfigResponseSchema.safeParse(data);
    // Only a real boolean true enables SSO; anything else defers to the allowlist.
    return parsed.success && parsed.data.ssoLoginEnabled === true;
  } finally {
    clearTimeout(timeout);
  }
};

/**
 * Fetch and apply this host's login mode. Never rejects and never blocks
 * longer than LOGIN_MODE_FETCH_TIMEOUT_MS — a failed or slow lookup falls back to the
 * cached value (else false, i.e. defer to the allowlist), because the login
 * page must always render. The host is passed in to avoid a circular import
 * with tenant.ts, which imports this module.
 */
export const hydrateLoginMode = async (host: string): Promise<void> => {
  if (!API_URL || !host) {
    // No host segment (bare localhost) means no per-host flag to read.
    ssoLoginEnabledForHost = readCachedLoginMode(host) ?? false;
    return;
  }
  try {
    ssoLoginEnabledForHost = await fetchLoginMode(host);
    writeCachedLoginMode(host, ssoLoginEnabledForHost);
  } catch {
    // Backend unreachable, slow, or pre-dating the endpoint (404): the last
    // known value keeps the login mode stable across transient outages rather
    // than flapping between SSO and the password form.
    ssoLoginEnabledForHost = readCachedLoginMode(host) ?? false;
  }
};

/**
 * Record the login mode for the NEXT load from an already-authenticated source.
 *
 * `GET /auth/tenant-info` carries `ssoLoginEnabled` and is fetched on every
 * signed-in load, so writing the cache from there keeps a signed-in user's mode
 * fresh without any extra request — which is why index.tsx only needs the
 * public endpoint before a session exists.
 *
 * Like {@link refreshCachedLoginMode}, this deliberately does not touch the
 * live value; see that function for why.
 */
export const cacheLoginMode = (host: string, value: boolean): void => {
  writeCachedLoginMode(host, value);
};

/**
 * Refresh the cached login mode for the NEXT load, after the app has rendered.
 *
 * Deliberately does not touch the live value: SuperTokens has already been
 * initialised for this session against the primed mode, so changing it
 * mid-session would leave the SDK and the route tree disagreeing. A flag flip
 * therefore takes effect on the following page load.
 */
export const refreshCachedLoginMode = async (host: string): Promise<void> => {
  if (!API_URL || !host) return;
  try {
    writeCachedLoginMode(host, await fetchLoginMode(host));
  } catch {
    // Best effort: the cached value stays as-is and we retry next load.
  }
};
